import React, {useEffect} from "react";
import Grid from "@mui/material/Grid";
import {useTheme} from "@mui/material/styles";
import {useLocation, useParams} from "react-router-dom";
import {useTranslation} from "react-i18next";

import Trainer from "../training/Trainer";
import Inspector from "../Inspector";
import QualityCheck from "../QualityCheck";
import Analyzer from "../analyze/Analyzer";
import {UserContext} from "../../App";
import Runs from "../Runs";
import Error from "../Error";
import PipelineStatus from "../training/PipelineStatus";
import {updateURL} from "../../utils/routing_utils";
import ExperimentHeader from "./ExperimentHeader";


export default function ExperimentRoutes(props) {
    // init internationalization
    const {t} = useTranslation();
    const theme = useTheme();

    // get data from current dataset
    const {
        datasets,
        currentDatasetIdx,
        setCurrentDatasetIdx,
        experiments,
        currentExperimentIdx,
        setCurrentExperimentIdx,
    } = React.useContext(UserContext);

    // get current URL
    const location = useLocation();
    const {dataset, experiment} = useParams();
    const [errorMessage, setErrorMessage] = React.useState(t("currentExperimentSelector.NoExperiment"));

    const updateExperimentByURL = (exp, locationHasChanged) => {
        // decode special chars and set uppercase to prevent caseSensivity issues
        const cleanedExpName = decodeURIComponent(exp).toUpperCase();
        if (
            cleanedExpName ===
            experiments[currentExperimentIdx].experimentName.toUpperCase()
        ) {
            setErrorMessage(null);
        }
        // check if route has changed with terms of the current experiment
        if (
            cleanedExpName !==
            experiments[currentExperimentIdx].experimentName.toUpperCase()
        ) {
            const expNames = experiments.map((dict) =>
                dict.experimentName.toUpperCase()
            );
            // check if experiment name exists for the user
            if (expNames.indexOf(cleanedExpName) !== -1) {
                setCurrentExperimentIdx(expNames.indexOf(cleanedExpName));
                setErrorMessage(null);
            }
            // if the user has entered an invalid exp via URL, show specific error message
            else if (locationHasChanged) {
                setErrorMessage(
                    `${t("experimentRoutes.MissingExperiment_1")} ${cleanedExpName} ${t(
                        "MissingExperiment_1.MissingExperiment_2"
                    )}`
                );
            }
        } else {
            setErrorMessage(null);
        }

    };

    const updateDatasetByURL = (ds) => {
        // decode special chars and set uppercase to prevent caseSensivity issues
        const cleanedDsName = decodeURIComponent(ds).toUpperCase();
        if (
            cleanedDsName === datasets[currentDatasetIdx].datasetName.toUpperCase()
        ) {
            setErrorMessage(null);
        }
        // check if route has changed with terms of the current dataset
        if (
            cleanedDsName !== datasets[currentDatasetIdx].datasetName.toUpperCase()
        ) {
            // use uppercase, since names should not be caseSensitive
            const dsNames = datasets.map((dict) => dict.datasetName.toUpperCase());
            // check if dataset name exists for the user
            if (dsNames.indexOf(cleanedDsName) !== -1) {
                setCurrentDatasetIdx(dsNames.indexOf(cleanedDsName));
            } else {
                // if the user has entered an invalid ds via URL, show specific page
                setErrorMessage(
                    `${t("experimentRoutes.MissingDataset_1")} ${cleanedDsName} ${t(
                        "experimentRoutes.MissingDataset_2"
                    )}`
                );
            }
        } else {
            setErrorMessage(null);
        }
    };

    // Callback method, everytime the URL changes
    useEffect(() => {
        if (props.route !== "NoExperiment") {
            setErrorMessage(null);
            // if ds and exp are given through route
            if (dataset && experiment && props.route) {
                updateDatasetByURL(dataset);
                updateExperimentByURL(experiment, true);
            } else {
                // if no dataset was given to route, set to latest
                updateURL(datasets, currentDatasetIdx, experiments, currentExperimentIdx);
            }
        }
    }, [location.pathname]);

    // Callback Method, everytime the experiment list changes
    useEffect(() => {
        if (props.route !== "NoExperiment") {
            setErrorMessage(null);
            // if ds and exp are given through route
            if (dataset && experiment) {
                updateExperimentByURL(experiment, false);
            } else {
                // if no dataset was given to route, set to latest
                updateURL(datasets, currentDatasetIdx, experiments, currentExperimentIdx);
            }
        }

    }, [...[experiments]]);

    return (
        <div>
            <Grid
                id="experiment-container"
                container
                alignItems="flex-start"
                justifyContent='flex-start'
                sx={{
                    padding: theme.spacing(6, 0),
                    borderRadius: "16px",
                }}
            >
                <ExperimentHeader/>
                {errorMessage ? (
                    <Error message={errorMessage}/>
                ) : (
                    {
                        // render Experiment Page, dependent on parent-route
                        qualityCheck: <QualityCheck/>,
                        inspect: <Inspector/>,
                        train: <Trainer/>,
                        analyze: <Analyzer/>,
                        pipeline: <PipelineStatus/>,
                        runs: <Runs/>,
                    }[props.route]
                )}
            </Grid>
        </div>
    );
}
